import type { Edge, Node } from 'reactflow';
import type { NodeData } from './CommonNode/CommonNode';
import type { TriggerNodeData } from './TriggerNode/TriggerNode';
import type { TriggerSettings } from './triggerWorkflowTypes';
import type { FlowAgentPersistedData, LlmBackboneId } from './agentWorkflowTypes';
import { slugifyAgentNodeName } from './agentWorkflowTypes';
import type { FlowActionPersistedData, ActionKindId } from './actionWorkflowTypes';
import type { FlowFxPersistedData, FlowFxKindId } from './flowFxWorkflowTypes';
import type { FlowLlmNodePersistedData, McpStep } from './llmWorkflowTypes';
import { slugifyNodeName } from './llmWorkflowTypes';

type FlowTriggerPersistedData = TriggerNodeData & { settings: TriggerSettings };

/** Re-exported shape used by the JSON preview modal (execution document v1). */
export type WorkflowExecutionDocumentV1 = ReturnType<typeof buildWorkflowExecutionDocumentV1>;

export type BuildWorkflowExecutionOptions = {
  /** Saved workflow id from route/API → `sourceFlowId` as `flow_<id>`. */
  sourceFlowId?: string | null;
  /** Tenant uuid (e.g. from workflow GET `tenant_id`). */
  tenantId?: string | null;
};

function isFlowTargetHandle(handle: string | null | undefined): boolean {
  if (handle == null || handle === 'in') return true;
  return /^in-\d+$/.test(handle);
}

function outgoingTargets(nodeId: string, edges: Edge[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const e of edges) {
    if (e.source !== nodeId) continue;
    if (seen.has(e.target)) continue;
    seen.add(e.target);
    out.push(e.target);
  }
  return out;
}

function incomingSources(nodeId: string, edges: Edge[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const e of edges) {
    if (e.target !== nodeId || !isFlowTargetHandle(e.targetHandle)) continue;
    if (seen.has(e.source)) continue;
    seen.add(e.source);
    out.push(e.source);
  }
  return out;
}

function upstreamOutputRef(nodeId: string, edges: Edge[], nodes: Node[]): string {
  const incoming = edges.filter((e) => e.target === nodeId && isFlowTargetHandle(e.targetHandle));
  if (incoming.length === 0) return '{{ previous.output }}';
  const src = nodes.find((n) => n.id === incoming[0].source);
  if (!src) return '{{ previous.output }}';
  if (src.type === 'llmNode') {
    const d = src.data as FlowLlmNodePersistedData;
    return `{{ ${d.nodeName}.output }}`;
  }
  if (src.type === 'agentNode') {
    const d = src.data as FlowAgentPersistedData;
    return `{{ ${d.nodeName}.output }}`;
  }
  if (src.type === 'triggerNode') return '{{ trigger.payload }}';
  if (src.type === 'actionNode') {
    const d = src.data as FlowActionPersistedData;
    return `{{ ${d.nodeName}.result }}`;
  }
  if (src.type === 'flowFxNode') {
    const d = src.data as FlowFxPersistedData;
    if (d.kind === 'comment') return '{{ previous.output }}';
    return `{{ ${d.nodeName}.output }}`;
  }
  if (src.type === 'commonNode') {
    const d = src.data as NodeData;
    return `{{ ${slugifyAgentNodeName(d.label)}.output }}`;
  }
  return '{{ previous.output }}';
}

function llmProviderModel(paletteType: string): { provider: string; model: string } {
  const t = paletteType || '';
  if (/Claude|Sonnet|claude/i.test(t)) return { provider: 'anthropic', model: 'claude-3-5-sonnet-latest' };
  if (/Gemini|gemini/i.test(t)) return { provider: 'google', model: 'gemini-1.5-pro' };
  if (/GPT|gpt|OpenAI|4o/i.test(t)) return { provider: 'openai', model: 'gpt-4o' };
  return { provider: 'openai', model: 'gpt-4o-mini' };
}

/** Maps agent backbone to execution `config.model` label. */
function backboneToModel(b: LlmBackboneId): string {
  switch (b) {
    case 'claude-sonnet':
      return 'claude-3-5-sonnet';
    case 'gemini':
      return 'gemini-1.5-pro';
    case 'gpt-4o':
    default:
      return 'gpt-4o';
  }
}

/**
 * Nested `category` on nodes/tools: sidebar category id, control uuid, category row uuid.
 */
function categoryFromRegistry(data: Record<string, unknown>): Record<string, string> | undefined {
  const id = data.workflowCategoryid;
  const controlId = data.workflowControlid;
  const uuid = data.workflowCategoryuuid;
  const out: Record<string, string> = {};
  if (typeof id === 'string' && id.trim()) out.id = id.trim();
  if (typeof controlId === 'string' && controlId.trim()) out.controlId = controlId.trim();
  if (typeof uuid === 'string' && uuid.trim()) out.uuid = uuid.trim();
  return Object.keys(out).length ? out : undefined;
}

function toolIdFromStep(step: McpStep): string {
  if (step.id.startsWith('mcp-')) return step.id;
  return `mcp-${step.id}`;
}

/** Output field names from the LLM config panel (default + MCP keys + extras); values are placeholders for downstream consumers. */
function llmOutputVariablesRecord(d: FlowLlmNodePersistedData): Record<string, string> {
  const used = new Set<string>();
  const out: Record<string, string> = {};
  const add = (key: string) => {
    if (used.has(key)) return;
    used.add(key);
    out[key] = '';
  };
  add('output');
  for (const s of d.mcpSteps) {
    add(s.outputKey);
  }
  if (d.mcpSteps.some((s) => s.kind === 'event')) {
    add('event_emitted');
  }
  for (const raw of d.extraOutputKeys ?? []) {
    const k = slugifyNodeName(raw).replace(/^_+|_+$/g, '') || 'extra';
    add(k);
  }
  return out;
}

function mcpStepToTool(step: McpStep): Record<string, unknown> {
  const id = toolIdFromStep(step);
  const stepData = step as unknown as Record<string, unknown>;
  const category = categoryFromRegistry(stepData);
  const base = (extra: Record<string, unknown>) => ({
    id,
    ...extra,
    ...(category ? { category } : {}),
  });

  switch (step.kind) {
    case 'knowledge_base':
      return base({
        type: 'knowledge_base_read',
        config: {
          kbName: step.knowledgeBase?.kbName ?? '',
          query: step.knowledgeBase?.queryTemplate ?? '{{input}}',
        },
        outputKey: step.outputKey,
      });
    case 'api':
      return base({
        type: 'http_request',
        config: {
          endpointRef: step.api?.endpointRef ?? '',
        },
        outputKey: step.outputKey,
      });
    case 'event':
      return base({
        type: 'event_emit',
        config: {
          eventName: step.event?.eventName ?? '',
          payloadTemplate: step.event?.payloadTemplate ?? '',
        },
        outputKey: step.outputKey,
      });
    case 'database':
    default:
      return base({
        type: 'database_read',
        config: {
          table: step.database?.table ?? '',
          query: step.database?.query ?? '{{default}}',
          ...(step.database?.operation ? { operation: step.database.operation } : {}),
          ...(step.manualInputTemplate != null ? { manualInputTemplate: step.manualInputTemplate } : {}),
          ...(step.manualOutputTemplate != null ? { manualOutputTemplate: step.manualOutputTemplate } : {}),
        },
        outputKey: step.outputKey,
      });
  }
}

/** Flat schema like `{ "result": "string" }` from agent output JSON text. */
function agentOutputSchemaSimple(schemaJson: string): Record<string, string> {
  try {
    const o = JSON.parse(schemaJson) as Record<string, unknown>;
    if (o && typeof o === 'object' && !Array.isArray(o)) {
      const out: Record<string, string> = {};
      for (const [k, v] of Object.entries(o)) {
        out[k] = typeof v === 'string' ? v : JSON.stringify(v);
      }
      if (Object.keys(out).length) return out;
    }
  } catch {
    /* ignore */
  }
  return { result: 'string' };
}

function actionConfigSummary(kind: ActionKindId, d: FlowActionPersistedData): Record<string, unknown> {
  switch (kind) {
    case 'send-email':
      return {
        to: d.emailToLines.filter(Boolean),
        subject: d.emailSubject,
        bodyFormat: d.emailBodyFormat,
      };
    case 'send-slack':
      return { channel: d.slackChannel, format: d.slackFormat };
    case 'http-request':
      return {
        method: d.httpMethod,
        url: d.httpUrl,
        bodyFormat: d.httpBodyFormat,
        headers: d.httpHeaders.map(({ key, value }) => ({ key, value })),
      };
    case 'update-crm':
      return { entity: d.crmEntity, recordId: d.crmRecordId };
    case 'emit-event':
      return { eventName: d.emitEventName };
    case 'create-record':
      return { entity: d.createEntity };
    case 'delete-record':
      return { entity: d.deleteEntity, recordId: d.deleteRecordId };
    case 'logger':
      return { level: d.logLevel };
    case 'wait':
      return { delayType: d.waitDelayType, duration: d.waitDuration, unit: d.waitUnit };
    case 'condition-check':
      return { expression: d.conditionExpr };
    case 'html-viewer':
      return { contentType: 'text/html', accepts: 'html-only' };
    default:
      return {};
  }
}

function flowFxConfigSummary(kind: FlowFxKindId, d: FlowFxPersistedData): Record<string, unknown> {
  switch (kind) {
    case 'if-else':
      return { condition: d.ifConditionExpr, trueLabel: d.ifTrueLabel, falseLabel: d.ifFalseLabel };
    case 'switch':
      return { expression: d.switchExpr, cases: d.switchCases };
    case 'loop':
      return {
        arrayExpr: d.loopArrayExpr,
        itemVar: d.loopItemVar,
        parallel: d.loopParallel,
      };
    case 'parallel':
      return { branchCount: d.parallelBranchCount, labels: d.parallelLabels.slice(0, d.parallelBranchCount) };
    case 'merge':
      return { strategy: d.mergeStrategy, inputCount: d.mergeInputCount };
    case 'end':
      return { returnKind: d.endReturnKind, status: d.endStatus };
    case 'set-variable':
      return { variables: d.setVariableRows };
    case 'transform':
      return { transformType: d.transformType, inputExpr: d.transformInputExpr };
    case 'logger':
      return { level: d.utilLogLevel, message: d.utilLogMessage };
    case 'comment':
      return { title: d.commentTitle, body: d.commentBody };
    default:
      return {};
  }
}

function flowNext(next: string[]): { flow: { next: string[] } } {
  return { flow: { next } };
}

/**
 * Execution document aligned with Selfic execution/v1 preview (transformed from React Flow canvas).
 */
export function buildWorkflowExecutionDocumentV1(
  nodes: Node[],
  edges: Edge[],
  options: BuildWorkflowExecutionOptions = {}
): Record<string, unknown> {
  const execId = `exec_${Date.now()}`;
  const source =
    options.sourceFlowId?.trim() ?
      `flow_${options.sourceFlowId.trim()}`
    : `flow_local_${execId.replace(/^exec_/, '')}`;

  const tenantId = options.tenantId?.trim();
  const triggerNode = nodes.find((n) => n.type === 'triggerNode');
  const entrypoint = triggerNode?.id ?? '';

  const nodeMap: Record<string, Record<string, unknown>> = {};

  for (const n of nodes) {
    const next = outgoingTargets(n.id, edges);
    const dependsOn = incomingSources(n.id, edges);
    const data = (n.data ?? {}) as Record<string, unknown>;
    const category = categoryFromRegistry(data);

    if (n.type === 'triggerNode') {
      const d = n.data as FlowTriggerPersistedData;
      const settings = d.settings;
      let variables: unknown[] = [];
      if (settings && settings.kind === 'manual') {
        variables = (settings as { variables?: unknown[] }).variables ?? [];
      }
      nodeMap[n.id] = {
        type: 'trigger',
        kind: d.triggerKind,
        ...(category ? { category } : {}),
        config: { variables },
        ...flowNext(next),
        output: { key: 'trigger' },
      };
      continue;
    }

    if (n.type === 'llmNode') {
      const d = n.data as FlowLlmNodePersistedData;
      const { provider, model } = llmProviderModel(d.paletteType);
      const tools = d.mcpSteps.map((s) => mcpStepToTool(s));
      nodeMap[n.id] = {
        type: 'llm',
        kind: 'analysis',
        ...(category ? { category } : {}),
        dependsOn,
        ...flowNext(next),
        config: {
          provider,
          model,
          temperature: d.temperature,
          maxTokens: d.maxTokens,
          prompt: '',
          system: d.parentPrompt,
          input: upstreamOutputRef(n.id, edges, nodes),
        },
        tools,
        output: {
          key: 'llm',
          ...llmOutputVariablesRecord(d),
        },
      };
      continue;
    }

    if (n.type === 'agentNode') {
      const d = n.data as FlowAgentPersistedData;
      nodeMap[n.id] = {
        type: 'agent',
        kind: d.agentKind,
        ...(category ? { category } : {}),
        dependsOn,
        ...flowNext(next),
        config: {
          model: backboneToModel(d.backbone),
          objective: d.objective,
          input: upstreamOutputRef(n.id, edges, nodes),
          ...(d.inputMappingOverride?.trim() ? { inputMappingOverride: d.inputMappingOverride } : {}),
        },
        operations: d.operations.map((op) => ({
          id: op.id,
          type: op.kind,
        })),
        output: {
          key: 'agent',
          schema: agentOutputSchemaSimple(d.outputSchemaJson),
        },
      };
      continue;
    }

    if (n.type === 'actionNode') {
      const d = n.data as FlowActionPersistedData;
      nodeMap[n.id] = {
        type: 'action',
        kind: d.actionKind,
        ...(category ? { category } : {}),
        dependsOn,
        ...flowNext(next),
        config: {
          nodeName: d.nodeName,
          label: d.label,
          paletteType: d.paletteType,
          summary: actionConfigSummary(d.actionKind, d),
          input: upstreamOutputRef(n.id, edges, nodes),
          ...(d.actionKind === 'http-request' ?
            { httpHeaders: d.httpHeaders, httpBody: d.httpBody }
          : {}),
          ...(d.actionKind === 'html-viewer' ?
            { contentType: 'text/html', html: d.htmlViewerHtml ?? '' }
          : {}),
        },
        output: { key: 'action' },
      };
      continue;
    }

    if (n.type === 'flowFxNode') {
      const d = n.data as FlowFxPersistedData;
      nodeMap[n.id] = {
        type: 'flow',
        kind: d.kind,
        ...(category ? { category } : {}),
        categoryTag: d.categoryTag,
        dependsOn,
        ...flowNext(next),
        config: {
          nodeName: d.nodeName,
          label: d.label,
          ...flowFxConfigSummary(d.kind, d),
          ...(d.kind !== 'comment' ? { input: upstreamOutputRef(n.id, edges, nodes) } : {}),
        },
        output: { key: d.nodeName || 'flow' },
      };
      continue;
    }

    if (n.type === 'commonNode') {
      const d = n.data as NodeData;
      nodeMap[n.id] = {
        type: 'node',
        kind: 'common',
        ...(category ? { category } : {}),
        dependsOn,
        ...flowNext(next),
        config: {
          label: d.label,
          paletteType: d.type ?? 'unknown',
          input: upstreamOutputRef(n.id, edges, nodes),
        },
        output: { key: 'node' },
      };
      continue;
    }

    nodeMap[n.id] = {
      type: n.type ?? 'unknown',
      ...(category ? { category } : {}),
      dependsOn,
      ...flowNext(next),
      config: { label: typeof data.label === 'string' ? data.label : n.id },
    };
  }

  const edgeRows = edges.map((e) => ({
    id: e.id,
    from: e.source,
    to: e.target,
    ...(e.sourceHandle ? { sourceHandle: e.sourceHandle } : {}),
    ...(e.targetHandle ? { targetHandle: e.targetHandle } : {}),
  }));

  const workflow: Record<string, string> = {
    id: execId,
    sourceFlowId: source,
  };
  if (tenantId) workflow.tenantId = tenantId;

  return {
    $schema: 'https://selficai.com/schemas/execution/v1.json',
    version: '1.0.0',
    workflow,
    entrypoint,
    nodes: nodeMap,
    edges: edgeRows,
    execution: {
      strategy: 'topological',
      parallelism: {
        enabled: true,
        maxConcurrent: 5,
      },
    },
    retry: {
      maxAttempts: 3,
      backoffMs: 1000,
    },
  };
}

export function serializeWorkflowExecutionV1(
  nodes: Node[],
  edges: Edge[],
  options?: BuildWorkflowExecutionOptions
): string {
  return JSON.stringify(buildWorkflowExecutionDocumentV1(nodes, edges, options));
}
